import { FormInstance } from "antd";
import ErrorTypes from "error-handling/errorTypes";
import { ForumResponseModel, TimeSlotSessionResponseModel } from "generated/api";
import { TFunction } from "i18next";
import moment, { MomentInput } from "moment";
import { toast } from "react-toastify";
import { toWallClockMoment } from "utils/dateUtils";

type SessionFormValue = {
  duration?: [MomentInput, MomentInput];
};

export type DuplicateTimePeriod = {
  startTime?: string;
  endTime?: string;
};

type DuplicateExistingTimeSlot = {
  id?: number;
  description?: string;
  timeSlotType?: string | number;
  Id?: number;
  Description?: string;
  TimeSlotType?: string | number;
};

const getSessionTimeKeyPart = (value?: MomentInput) => {
  const date = toWallClockMoment(moment(value)).startOf("minute");

  return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : undefined;
};

export const applySequentialOrder = <T extends { order?: number }>(sourceSessions: T[]) =>
  sourceSessions.map((item, index) => ({ ...item, order: index + 1 }));

export const normalizeSessionOrder = <T extends { order?: number }>(sourceSessions: T[]) =>
  applySequentialOrder([...sourceSessions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));

export const getSessionKey = (session: TimeSlotSessionResponseModel) =>
  Number(session.id) > 0 ? `id-${session.id}` : `order-${session.order}`;

export const createDefaultSession = (forum: Pick<ForumResponseModel, "startDate">): TimeSlotSessionResponseModel => ({
  id: 0,
  order: 1,
  startTime: forum.startDate,
});

export const createSessionWithDefaultDuration = ({
  forum,
  order,
  defaultSessionDuration,
}: {
  forum?: Pick<ForumResponseModel, "startDate"> | null;
  order: number;
  defaultSessionDuration: number;
}): TimeSlotSessionResponseModel => {
  const startTime = forum?.startDate;

  return {
    id: 0,
    order,
    startTime,
    endTime: startTime
      ? defaultSessionDuration > 0
        ? moment(startTime).add(defaultSessionDuration, "minutes").format("YYYY-MM-DDTHH:mm:ss")
        : undefined
      : undefined,
  };
};

export const applyFormValuesToSessions = (
  form: FormInstance,
  sourceSessions: TimeSlotSessionResponseModel[],
): TimeSlotSessionResponseModel[] => {
  const sessionFields = (form.getFieldValue("sessions") ?? {}) as Record<string, SessionFormValue>;

  return sourceSessions.map((session) => {
    const duration = sessionFields[getSessionKey(session)]?.duration;

    if (!duration) {
      return session;
    }

    return {
      ...session,
      startTime: duration[0] ? moment(duration[0]).format("YYYY-MM-DDTHH:mm:ss") : undefined,
      endTime: duration[1] ? moment(duration[1]).format("YYYY-MM-DDTHH:mm:ss") : undefined,
    };
  });
};

export const showTimeslotValidationError = (t: TFunction) =>
  toast.error(String(t("Please resolve the validation errors before saving.")));

export const showDuplicateSessionTimeValidationError = (t: TFunction) =>
  toast.error(String(t("Duplicate session time. Please update the highlighted session rows.")), {
    autoClose: 7500,
  });

const getItemFields = (response: any) => response?.data?.meta?.ItemFields ?? response?.data?.meta?.itemFields ?? {};
const getField = (fields: Record<string, any>, capitalizedKey: string, lowerCamelKey: string) =>
  fields[capitalizedKey] ?? fields[lowerCamelKey];

const getNumberArrayField = (response: any, capitalizedKey: string, lowerCamelKey: string): number[] => {
  const fields = getItemFields(response);
  const ids = getField(fields, capitalizedKey, lowerCamelKey) ?? [];

  return Array.isArray(ids) ? ids.map(Number).filter((id) => id > 0) : [];
};

export const getDuplicateTimeSlotSessionIds = (response: any): number[] => {
  return getNumberArrayField(response, "TimeSlotSessionIds", "timeSlotSessionIds");
};

export const getExistingDuplicateTimeSlotSessionIds = (response: any): number[] =>
  getNumberArrayField(response, "ExistingTimeSlotSessionIds", "existingTimeSlotSessionIds");

export const getRequestedDuplicateTimeSlotSessionIds = (response: any): number[] =>
  getNumberArrayField(response, "RequestedTimeSlotSessionIds", "requestedTimeSlotSessionIds");

export const getSessionTimePeriods = (sourceSessions: TimeSlotSessionResponseModel[]): DuplicateTimePeriod[] =>
  sourceSessions
    .filter((session) => session.startTime && session.endTime)
    .map((session) => ({ startTime: session.startTime, endTime: session.endTime }));

export const getDuplicateSessionTimePeriods = (
  sourceSessions: TimeSlotSessionResponseModel[],
): DuplicateTimePeriod[] => {
  const periodsByKey = new Map<string, DuplicateTimePeriod & { count: number }>();

  sourceSessions
    .filter((session) => session.startTime && session.endTime)
    .forEach((session) => {
      const startTime = getSessionTimeKeyPart(session.startTime);
      const endTime = getSessionTimeKeyPart(session.endTime);

      if (!startTime || !endTime) {
        return;
      }

      const key = `${startTime}|${endTime}`;
      const existing = periodsByKey.get(key);

      periodsByKey.set(key, {
        startTime,
        endTime,
        count: existing ? existing.count + 1 : 1,
      });
    });

  return Array.from(periodsByKey.values())
    .filter((period) => period.count > 1)
    .map(({ startTime, endTime }) => ({ startTime, endTime }));
};

export const hasDuplicateSessionTimePeriodWithSavedSession = (
  sourceSessions: TimeSlotSessionResponseModel[],
): boolean => {
  const sessionsByPeriod = new Map<string, TimeSlotSessionResponseModel[]>();

  sourceSessions
    .filter((session) => session.startTime && session.endTime)
    .forEach((session) => {
      const startTime = getSessionTimeKeyPart(session.startTime);
      const endTime = getSessionTimeKeyPart(session.endTime);

      if (!startTime || !endTime) {
        return;
      }

      const key = `${startTime}|${endTime}`;

      sessionsByPeriod.set(key, [...(sessionsByPeriod.get(key) ?? []), session]);
    });

  return Array.from(sessionsByPeriod.values()).some(
    (sessions) => sessions.length > 1 && sessions.some((session) => Number(session.id) > 0),
  );
};

export const getDuplicateTimePeriods = (response: any): DuplicateTimePeriod[] => {
  const fields = getItemFields(response);
  const periods = getField(fields, "DuplicateTimePeriods", "duplicateTimePeriods");

  if (Array.isArray(periods)) {
    return periods.map((period) => ({
      startTime: period.StartTime ?? period.startTime,
      endTime: period.EndTime ?? period.endTime,
    }));
  }

  const startTimes = getField(fields, "StartTime", "startTime");
  const endTimes = getField(fields, "EndTime", "endTime");

  if (Array.isArray(startTimes) && Array.isArray(endTimes)) {
    return startTimes.map((startTime, index) => ({ startTime, endTime: endTimes[index] }));
  }

  return [];
};

const getExistingDuplicateTimeSlots = (response: any): DuplicateExistingTimeSlot[] => {
  const fields = getItemFields(response);
  const timeSlots = getField(fields, "ExistingTimeSlots", "existingTimeSlots");

  return Array.isArray(timeSlots) ? timeSlots : [];
};

const getTimeSlotLabel = (timeSlot: DuplicateExistingTimeSlot) => {
  const description = timeSlot.Description ?? timeSlot.description;
  const timeSlotType = timeSlot.TimeSlotType ?? timeSlot.timeSlotType;

  return [description, timeSlotType].filter(Boolean).join(" - ");
};

const getExistingDuplicateTimeSlotLabels = (response: any) =>
  getExistingDuplicateTimeSlots(response).map(getTimeSlotLabel).filter(Boolean);

export const isDuplicateSessionError = (response: any) =>
  ErrorTypes.isOfType(response, ErrorTypes.DuplicateItem) &&
  (response?.data?.meta?.ItemType ?? response?.data?.meta?.itemType) === "TimeSlotSession";

export const isDuplicateTimeSlotError = (response: any) =>
  ErrorTypes.isOfType(response, ErrorTypes.DuplicateItem) &&
  (response?.data?.meta?.ItemType ?? response?.data?.meta?.itemType) === "TimeSlot";

export const getDuplicateTimeSlotValidationMessage = (t: TFunction) =>
  String(t("Timeslot with this description and time type already exists for this forum."));

export const showDuplicateTimeSlotValidationError = (t: TFunction) =>
  toast.error(getDuplicateTimeSlotValidationMessage(t), {
    autoClose: 7500,
  });

export const handleTimeslotError = (response: any, t: TFunction) => {
  const meta = response?.data?.meta;

  if (ErrorTypes.isOfType(response, ErrorTypes.ObjectNotFound)) {
    toast.error(`${meta?.ObjectType} not found.`, { autoClose: 30000 });
  } else if (ErrorTypes.isOfType(response, ErrorTypes.InvalidTimePeriod)) {
    if (meta?.PropertyName === "ForumEndDate") {
      toast.error(String(t("errors.custom.sessionEndTimeAfterForumEndTime")), { autoClose: 7500 });
    } else if (meta?.PropertyName === "ForumStartDate") {
      toast.error(String(t("errors.custom.sessionStartTimeBeforeForumStartTime")), { autoClose: 7500 });
    } else {
      toast.error(String(t("errors.custom.sessionEndTimeBeforeStartTime")), { autoClose: 7500 });
    }
  } else if (ErrorTypes.isOfType(response, ErrorTypes.ObjectHasAssociatedItemsException)) {
    if (meta?.AssociatedObjects?.length > 0) {
      const associatedObj = meta.AssociatedObjects.map((obj: { ObjectFields: { Id: any } }) => {
        const id = obj?.ObjectFields?.Id;
        return String(t("Forum Seminar Session ID: {{id}}", { id }));
      }).join(", ");

      toast.error(
        String(
          t(
            "Cannot delete session(s) because they are associated with the following Forum Seminar Session(s): {{associatedObjects}}",
            { associatedObjects: associatedObj },
          ),
        ),
        { autoClose: 30000 },
      );
    } else {
      toast.error(String(t("Something went wrong")), { autoClose: 30000 });
    }
  } else if (isDuplicateTimeSlotError(response)) {
    showDuplicateTimeSlotValidationError(t);
  } else if (isDuplicateSessionError(response)) {
    if (getExistingDuplicateTimeSlotSessionIds(response).length) {
      const timeSlotLabels = getExistingDuplicateTimeSlotLabels(response);
      const message = timeSlotLabels.length
        ? t("Session time already exists in {{timeSlots}}. Please update the highlighted session rows.", {
            timeSlots: timeSlotLabels.join(", "),
          })
        : t("Session time already exists. Please update the highlighted session rows.");

      toast.error(String(message), {
        autoClose: 7500,
      });
    } else {
      showDuplicateSessionTimeValidationError(t);
    }
  } else {
    toast.error(String(t("Something went wrong")), { autoClose: 30000 });
  }
};
